/** The submission pack, read through the filing's regime rows.
 *
 * **EmitIQ prepares. The company submits.** The pack lists what the regulator's intake asks, each
 * figure with its receipts, each fact EmitIQ holds, and each question that is the company's to
 * answer with no value and no guess. Nothing here sends anything, and nothing here is named after
 * a regulator: every word that is specific to one arrives on a row, with its citation.
 *
 * Three rules the screen keeps:
 *
 * - **A question the company must answer is shown as that**, not as a blank field. "Yours to give"
 *   is a statement about the form; an empty cell is a statement about the data.
 * - **A figure is never shown without its receipt links and the line's own caveat.** Rule 3.
 * - **The statement a regime accepts in place of a filing is drafted only after a person says the
 *   fact is true** (`confirmed`), and is then an ordinary disclosure statement: versioned,
 *   approvable, never in the ledger as words.
 */

import { Link } from "@tanstack/react-router";
import { useState } from "react";

import type { Obligation, Scope2Method } from "../api/queries";
import type { SubmissionField, SubmissionOption, SubmissionPack } from "../api/submissionPack";
import { submissionExportUrl, useDraftLetter, useSubmissionPack } from "../api/submissionPack";
import { ReportingYearNote, WatchForNote } from "../components/FilingYearNote";
import { Loaded } from "../components/Loaded";
import { Panel } from "../components/Panel";
import { WindowLockBanner } from "../components/WindowLockBanner";
import { customerText, supportLevelWords } from "../lib/customerWords";
import { shortDate } from "../lib/format";
import { MAY_DRAFT } from "./mayDraft";
import { ServerNote } from "./filings/ServerNote";
import own from "./Reports.module.css";
import styles from "./ReportsSubmission.module.css";
import { amount, cls } from "./reportsFormat";
import { keepSearch } from "./search";

function Cite({ citation, url }: { citation: string; url: string }) {
  return (
    <span className={styles.cite}>
      <a href={url} target="_blank" rel="noreferrer">
        {citation}
      </a>
    </span>
  );
}

function Notes({ title, items, testId }: { title: string; items: SubmissionPack["notes"]; testId: string }) {
  if (items.length === 0) return null;
  return (
    <div className={styles.group} data-testid={testId}>
      <h3 className={styles.heading}>{title}</h3>
      <ul className={styles.list}>
        {items.map((item) => (
          <li key={item.key} className={styles.item} data-key={item.key}>
            <strong>{item.label}.</strong> {item.detail}
            <Cite citation={item.citation} url={item.source_url} />
            {item.limitation ? <span className={styles.limit}>{item.limitation}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LetterPanel({
  obligationId,
  option,
  canDraft,
}: {
  obligationId: string;
  option: SubmissionOption;
  canDraft: boolean;
}) {
  const draft = useDraftLetter(obligationId);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [edit, setEdit] = useState("");
  const letter = option.letter;
  const ready = confirmed && name.trim() !== "" && title.trim() !== "";
  return (
    <div data-testid="letter" data-option={option.key}>
      {letter ? (
        <>
          <pre className={styles.letter} data-testid="letter-text">
            {letter.text}
          </pre>
          <p className={own.fine} data-testid="letter-approval" data-approval={letter.approval}>
            {letter.approval === "approved"
              ? "A person has approved this version of the words."
              : letter.approval === "in_review"
                ? "This version is waiting for a reviewer."
                : "This is a draft. No one has approved this version."}{" "}
            Approval is a person&rsquo;s act on the statement&rsquo;s own page. You put the approved
            words on your letterhead and send them yourself.{" "}
            <Link
              to="/records/$recordId"
              params={{ recordId: letter.statement_id }}
              search={keepSearch}
              data-testid="letter-record"
            >
              Open the statement
            </Link>
          </p>
        </>
      ) : null}
      {canDraft ? (
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            if (!ready) return;
            draft.mutate({
              optionKey: option.key,
              signatoryName: name.trim(),
              signatoryTitle: title.trim(),
              confirmed,
              text: edit.trim() === "" ? undefined : edit,
            });
          }}
        >
          <label>
            Name of the person making the statement
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Their title
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className={styles.confirm}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="letter-confirm"
            />
            <span>
              I confirm that what the statement says, as quoted above, is true of our company.
              EmitIQ cannot see this, so it will not draft the statement until a person says so.
            </span>
          </label>
          <label>
            Or paste your own wording to replace the draft (optional)
            <textarea rows={4} value={edit} onChange={(e) => setEdit(e.target.value)} />
          </label>
          <button type="submit" disabled={!ready || draft.isPending} data-testid="letter-draft">
            {letter ? "Draft it again" : "Draft the statement"}
          </button>
          {draft.error ? (
            <p className={cls(own.notice, own.noticeWarn)} role="alert" data-testid="letter-error">
              {draft.error.message}
            </p>
          ) : null}
        </form>
      ) : (
        <p className={own.fine}>Your role can read this statement and not draft it.</p>
      )}
    </div>
  );
}

function Options({
  pack,
  obligationId,
  canDraft,
}: {
  pack: SubmissionPack;
  obligationId: string;
  canDraft: boolean;
}) {
  if (pack.options.length === 0) return null;
  return (
    <div className={styles.group} data-testid="pack-options">
      <h3 className={styles.heading}>What the regulator accepts</h3>
      <ul className={styles.list}>
        {pack.options.map((option) => (
          <li key={option.key} className={styles.item} data-key={option.key}>
            <strong>{option.label}.</strong> {option.detail}
            {option.only_for_due_date ? (
              <span className={styles.limit} data-testid="option-only-for">
                For the filing due {shortDate(option.only_for_due_date)} only.
              </span>
            ) : null}
            <Cite citation={option.citation} url={option.source_url} />
            {option.limitation ? <span className={styles.limit}>{option.limitation}</span> : null}
            {option.has_letter ? (
              <LetterPanel obligationId={obligationId} option={option} canDraft={canDraft} />
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Answer({ field }: { field: SubmissionField }) {
  if (field.entry === "company") {
    return <span className={styles.theirs}>Yours to give. EmitIQ holds nothing for this.</span>;
  }
  if (field.entry === "not_produced") {
    return (
      <span className={styles.theirs}>
        EmitIQ does not produce this.{field.limitation ? ` ${field.limitation}` : ""}
      </span>
    );
  }
  if (field.entry === "recorded") {
    return field.value ? (
      <span className={styles.value}>{field.value}</span>
    ) : (
      <span className={styles.theirs}>{field.unavailable_because ?? "Not recorded."}</span>
    );
  }
  if (field.value === null) {
    return (
      <span className={styles.theirs}>
        {field.unavailable_because ?? "No figure can be produced."}
      </span>
    );
  }
  return (
    <>
      <span className={styles.value} data-testid="field-value">
        {amount(field.value, field.unit)} {field.unit === "tCO2e" ? "tCO₂e" : field.unit}
      </span>
      <span className={styles.cite}>
        {field.records} {field.records === 1 ? "record" : "records"}
        {field.receipt_ids.length > 0 ? (
          <>
            {" · "}
            {field.receipt_ids.slice(0, 3).map((id, index) => (
              <span key={id}>
                {index > 0 ? ", " : ""}
                <Link
                  to="/reports"
                  search={(prev: Record<string, unknown>) => ({ ...keepSearch(prev), tab: typeof prev.tab === "string" ? prev.tab : undefined, receipt: id })}
                  data-testid="field-receipt"
                >
                  receipt {index + 1}
                </Link>
              </span>
            ))}
            {field.receipt_ids.length > 3 || field.receipt_ids_truncated
              ? ` and ${field.records - Math.min(3, field.receipt_ids.length)} more in the register`
              : ""}
          </>
        ) : field.records === 0 ? (
          " · nothing recorded for this period, so a zero, with no receipt to open"
        ) : null}
      </span>
      {field.line_limitation ? <span className={styles.limit}>{field.line_limitation}</span> : null}
    </>
  );
}

function Fields({ pack }: { pack: SubmissionPack }) {
  if (pack.fields.length === 0) return null;
  const sections: string[] = [];
  for (const field of pack.fields) {
    const section = field.section ?? "Other";
    if (!sections.includes(section)) sections.push(section);
  }
  return (
    <Panel
      title="What the intake asks"
      sub="Every question, in the regulator's own words, with where its answer comes from"
      column="1 / 13"
    >
      <div data-testid="pack-fields">
        {sections.map((section) => (
          <div key={section}>
            <h3 className={styles.section}>{section}</h3>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Question</th>
                  <th scope="col">Answer</th>
                </tr>
              </thead>
              <tbody>
                {pack.fields
                  .filter((field) => (field.section ?? "Other") === section)
                  .map((field) => (
                    <tr key={field.key} data-key={field.key} data-entry={field.entry}>
                      <td>
                        <strong>{field.label}</strong>
                        {field.voluntary ? " (optional)" : ""}
                        <span className={styles.question}>{field.question}</span>
                        <Cite citation={field.citation} url={field.source_url} />
                      </td>
                      <td>
                        <Answer field={field} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function PackBody({
  pack,
  active,
  role,
}: {
  pack: SubmissionPack;
  active: Obligation;
  role: string | undefined;
}) {
  return (
    <>
      <Panel
        title="Submission pack"
        sub={`${pack.legal_entity_name} · ${shortDate(pack.period_start)} – ${shortDate(pack.period_end)} · ${pack.scope2_method}-based Scope 2`}
        column="1 / 13"
      >
        <p className={cls(own.notice, own.noticeWarn)} data-testid="pack-statement">
          <span className={cls(own.noticeTitle)}>{supportLevelWords(pack.support_level)}</span>
          {customerText(pack.statement)}
        </p>
        {pack.status !== "required" ? (
          <p className={cls(own.notice, own.noticeWarn)} data-testid="pack-not-owed">
            This filing is recorded as {pack.status.replace(/_/g, " ")}, so nothing is owed on it.
            The pack shows what would be handed over.
          </p>
        ) : null}
        {pack.in_force_caveat ? (
          <p className={cls(own.notice, own.noticeWarn)} data-testid="pack-in-force">
            <span className={cls(own.noticeTitle)}>{pack.in_force_label}</span>
            {pack.in_force_caveat}
          </p>
        ) : null}
        <WindowLockBanner lock={pack.window_lock} />
        {pack.gwp_basis.length > 0 ? (
          <p className={own.fine} data-testid="pack-gwp">
            Figures are priced under{" "}
            {pack.gwp_basis
              .map(
                (basis) =>
                  `${basis.label ?? "a basis not recorded"}${basis.publisher ? `, ${basis.publisher}` : ""}${basis.edition ? ` ${basis.edition}` : ""}`,
              )
              .join("; ")}
            . {pack.regime_name} is read at rules version {pack.regime_version}.
          </p>
        ) : null}
        {pack.template_export ? (
          <p className={own.fine} data-testid="pack-export">
            <a
              href={submissionExportUrl(active.obligation_id, pack.scope2_method as Scope2Method)}
              download
              data-testid="pack-export-link"
            >
              {pack.template_export.label}
            </a>{" "}
            {pack.template_export.detail}
          </p>
        ) : null}
        {pack.due_date ? (
          <p className={own.fine} data-testid="pack-due">
            <strong>Due {shortDate(pack.due_date)}.</strong>{" "}
            <ServerNote label="Why this date" title="Why this date" text={pack.due_date_basis} testId="pack-due-why" />
          </p>
        ) : null}
        <ReportingYearNote year={pack.reporting_year} testId="pack-reporting-year" />
        <WatchForNote items={pack.watch_for} testId="pack-watch-for" />
        {!pack.has_rows ? (
          <p className={cls(own.notice, own.noticeWarn)} data-testid="pack-empty">
            No source has been read for how this filing is handed over, so this pack lists nothing.
            That is not a statement that nothing is required.
          </p>
        ) : null}
        <Notes title="Who submits" items={pack.submitters} testId="pack-submitters" />
        {pack.routes.length > 0 ? (
          <div className={styles.group} data-testid="pack-routes">
            <h3 className={styles.heading}>Where it goes</h3>
            <ul className={styles.list}>
              {pack.routes.map((route) => (
                <li key={route.key} className={styles.item} data-key={route.key}>
                  <strong>{route.label}</strong>
                  {route.voluntary ? " (the regulator does not make this compulsory)" : ""}.{" "}
                  {route.detail}{" "}
                  {route.channel === "email" ? (
                    <a href={`mailto:${route.address}`}>{route.address}</a>
                  ) : (
                    <a href={route.address} target="_blank" rel="noreferrer">
                      Open the form
                    </a>
                  )}
                  <Cite citation={route.citation} url={route.source_url} />
                  {route.limitation ? <span className={styles.limit}>{route.limitation}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <Options
          pack={pack}
          obligationId={active.obligation_id}
          canDraft={role !== undefined && MAY_DRAFT.has(role)}
        />
        <Notes title="Also" items={pack.notes} testId="pack-notes" />
      </Panel>
      <Fields pack={pack} />
    </>
  );
}

export function SubmissionTab({
  active,
  method,
  role,
}: {
  active: Obligation;
  method: Scope2Method;
  role: string | undefined;
}) {
  const read = useSubmissionPack(active.obligation_id, method);
  return (
    <Loaded query={read} of="This filing's submission pack" empty="No submission pack for this filing.">
      {(pack) => <PackBody pack={pack} active={active} role={role} />}
    </Loaded>
  );
}
